/**
 * VideoTrack - Displays video clips on the timeline with drag, trim, and reorder support
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { Film, AlertCircle, GripVertical, ChevronDown, ChevronRight } from 'lucide-react';
import { getEffectiveDuration } from '../utils/clipUtils';

export function VideoTrack({
  clips,
  shots,
  totalDuration,
  pxPerMs,
  totalWidth,
  labelWidth,
  playheadPosition,
  selectedClipIds,
  onSelectClip,
  onMoveClip,
  onTrimClip,
  onReorderClips,
  onClick,
  getAssetUrl,
  isCollapsed = false,
  onToggleCollapse
}) {
  const [dragState, setDragState] = useState(null); // { type: 'move' | 'trim-start' | 'trim-end', clipId, startX, originalValue }
  const [dropIndicator, setDropIndicator] = useState(null); // { index, x } for reorder indicator
  const trackRef = useRef(null);

  // Find shot data for a clip
  const getShotForClip = useCallback((clip) => {
    if (!clip?.scene_id) return null;
    return shots?.find(s => s.id === clip.scene_id);
  }, [shots]);

  // Get current take for clip
  const getTakeForClip = useCallback((clip, shot) => {
    if (!clip?.take_id || !shot) return null;
    return shot.takes?.find(t => t.id === clip.take_id);
  }, []);

  // Handle mouse move for dragging/trimming
  const handleMouseMove = useCallback((e) => {
    if (!dragState || !trackRef.current) return;
    
    const rect = trackRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const deltaX = currentX - dragState.startX;
    const deltaMs = deltaX / pxPerMs;
    
    if (dragState.type === 'move') {
      const newStartMs = Math.max(0, dragState.originalValue + deltaMs);
      onMoveClip?.(dragState.clipId, 'video', Math.round(newStartMs));
      
      // Calculate drop position for reorder indicator
      const clip = clips.find(c => c.id === dragState.clipId);
      if (clip) {
        const centerX = (clip.start_ms + clip.duration_ms / 2) * pxPerMs;
        let dropIndex = clips.length;
        for (let i = 0; i < clips.length; i++) {
          const c = clips[i];
          if (c.id === clip.id) continue;
          const clipCenterX = (c.start_ms + c.duration_ms / 2) * pxPerMs;
          if (centerX < clipCenterX) {
            dropIndex = i;
            break;
          }
        }
        setDropIndicator({ index: dropIndex, x: currentX });
      }
    } else if (dragState.type === 'trim-start') {
      const clip = clips.find(c => c.id === dragState.clipId);
      if (clip) {
        const newTrimStart = Math.max(0, Math.min(
          dragState.originalTrimStart + deltaMs,
          clip.duration_ms - 100 // Minimum 100ms remaining
        ));
        onTrimClip?.(dragState.clipId, 'video', { trim_start_ms: Math.round(newTrimStart) });
      }
    } else if (dragState.type === 'trim-end') {
      const clip = clips.find(c => c.id === dragState.clipId);
      if (clip) {
        const newTrimEnd = Math.max(0, Math.min(
          dragState.originalTrimEnd - deltaMs,
          clip.duration_ms - clip.trim_start_ms - 100
        ));
        onTrimClip?.(dragState.clipId, 'video', { trim_end_ms: Math.round(newTrimEnd) });
      }
    }
  }, [dragState, pxPerMs, clips, onMoveClip, onTrimClip]);

  // Handle mouse up - end drag
  const handleMouseUp = useCallback(() => {
    if (dragState?.type === 'move' && dropIndicator) {
      // Trigger reorder if needed
      onReorderClips?.(dragState.clipId, dropIndicator.index);
    }
    setDragState(null);
    setDropIndicator(null);
  }, [dragState, dropIndicator, onReorderClips]);

  // Add global mouse handlers when dragging
  useEffect(() => {
    if (dragState) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState, handleMouseMove, handleMouseUp]);

  // Start move drag
  const handleMoveStart = useCallback((e, clip) => {
    e.stopPropagation();
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    setDragState({
      type: 'move',
      clipId: clip.id,
      startX: e.clientX - rect.left,
      originalValue: clip.start_ms
    });
    onSelectClip?.(clip.id, 'video', e.shiftKey || e.metaKey || e.ctrlKey);
  }, [onSelectClip]);

  // Start trim drag
  const handleTrimStart = useCallback((e, clip, edge) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    setDragState({
      type: edge === 'start' ? 'trim-start' : 'trim-end',
      clipId: clip.id,
      startX: e.clientX - rect.left,
      originalTrimStart: clip.trim_start_ms || 0,
      originalTrimEnd: clip.trim_end_ms || 0
    });
    onSelectClip?.(clip.id, 'video', false);
  }, [onSelectClip]);

  // Handle click on track background
  const handleTrackClick = useCallback((e) => {
    if (e.target === trackRef.current || e.target.classList.contains('track-background')) {
      onClick?.(e);
    }
  }, [onClick]);

  return (
    <div 
      className={`${isCollapsed ? 'h-8' : 'h-14'} bg-zinc-900/50 border-b border-zinc-800 flex shrink-0 transition-all duration-200`}
      onClick={handleTrackClick}
    >
      {/* Track label - sticky on left */}
      <div 
        className="shrink-0 border-r border-zinc-800 flex items-center px-2 bg-zinc-900/80 gap-1 sticky left-0 z-10"
        style={{ width: labelWidth }}
      >
        <button 
          onClick={(e) => { e.stopPropagation(); onToggleCollapse?.(); }}
          className="p-0.5 hover:bg-zinc-700 rounded text-zinc-500 hover:text-zinc-300"
        >
          {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        <Film className="w-4 h-4 text-indigo-400" />
        <span className="text-xs text-zinc-400">Video</span>
      </div>
      
      {/* Clips area */}
      <div 
        ref={trackRef}
        className="relative flex-1 overflow-hidden track-background"
        style={{ width: totalWidth }}
      >
        {!isCollapsed && (
          <>
            {/* Drop indicator line */}
            {dropIndicator && (
              <div 
                className="absolute top-0 bottom-0 w-1 bg-indigo-500 z-30 pointer-events-none"
                style={{ left: dropIndicator.x }}
              />
            )}
        
        {clips?.map((clip, index) => {
          const shot = getShotForClip(clip);
          const take = getTakeForClip(clip, shot);
          const isSelected = selectedClipIds?.includes(clip.id);
          const isDragging = dragState?.clipId === clip.id;
          const hasVideo = !!take?.video_url;
          const hasThumbnail = !!(shot?.composite_image_url || shot?.preview_url);
          
          // Calculate effective duration after trimming and speed adjustment
          const effectiveDuration = getEffectiveDuration(clip);
          const clipWidth = effectiveDuration * pxPerMs;
          const clipLeft = clip.start_ms * pxPerMs;
          
          return (
            <div
              key={clip.id}
              className={`absolute top-1 bottom-1 rounded transition-shadow select-none ${
                isDragging
                  ? 'cursor-grabbing opacity-80 z-20'
                  : 'cursor-grab'
              } ${
                isSelected
                  ? 'ring-2 ring-white shadow-lg z-10'
                  : 'hover:ring-1 hover:ring-zinc-500'
              } ${
                clip.is_blank
                  ? 'bg-zinc-800 border border-dashed border-zinc-600'
                  : hasVideo
                    ? 'bg-indigo-600/80'
                    : 'bg-amber-600/60'
              }`}
              style={{
                left: clipLeft,
                width: Math.max(clipWidth, 4)
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!dragState) {
                  onSelectClip?.(clip.id, 'video', e.shiftKey || e.metaKey || e.ctrlKey);
                }
              }}
              onMouseDown={(e) => handleMoveStart(e, clip)}
              data-testid={`video-clip-${index}`}
            >
              {/* Thumbnail with tiling effect */}
              {clipWidth > 30 && !clip.is_blank && (
                <div className="absolute inset-0 overflow-hidden rounded">
                  {hasThumbnail ? (
                    <div 
                      className="h-full w-full"
                      style={{
                        backgroundImage: `url(${getAssetUrl(shot.composite_image_url || shot.preview_url)})`,
                        backgroundSize: 'auto 100%',
                        backgroundRepeat: 'repeat-x',
                        backgroundPosition: 'left center',
                        opacity: 0.5
                      }}
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center bg-zinc-700/50">
                      <AlertCircle className="w-4 h-4 text-zinc-500" />
                    </div>
                  )}
                </div>
              )}
              
              {/* Clip label */}
              {clipWidth > 50 && (
                <div className="absolute inset-x-0 bottom-0 p-1 bg-gradient-to-t from-black/50 to-transparent">
                  <span className="text-[10px] text-white font-medium truncate block drop-shadow-lg">
                    {clip.is_blank ? 'Blank' : shot?.name || `Shot ${index + 1}`}
                  </span>
                </div>
              )}
              
              {/* Duration badge */}
              {clipWidth > 80 && (
                <div className="absolute top-1 right-1 bg-black/50 px-1 rounded text-[9px] text-zinc-300">
                  {Math.round(effectiveDuration / 1000)}s
                </div>
              )}
              
              {/* Trim handles - only show on selected clips */}
              {isSelected && clipWidth > 20 && (
                <>
                  {/* Left trim handle */}
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-3 bg-white/0 hover:bg-white/30 cursor-ew-resize rounded-l group z-30"
                    onMouseDown={(e) => handleTrimStart(e, clip, 'start')}
                  >
                    <div className="absolute left-0.5 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/50 rounded group-hover:bg-white transition-colors" />
                  </div>
                  
                  {/* Right trim handle */}
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-3 bg-white/0 hover:bg-white/30 cursor-ew-resize rounded-r group z-30"
                    onMouseDown={(e) => handleTrimStart(e, clip, 'end')}
                  >
                    <div className="absolute right-0.5 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/50 rounded group-hover:bg-white transition-colors" />
                  </div>
                </>
              )}
              
              {/* Drag handle indicator (center) */}
              {isSelected && clipWidth > 60 && (
                <div className="absolute top-1 left-1/2 -translate-x-1/2 opacity-50">
                  <GripVertical className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          );
        })}
          </>
        )}
      </div>
    </div>
  );
}
